import { RefObject } from "react";

import { PATHS } from "src/core/constants";
import { CliCommandCompletion } from "src/gen/schema/bilge/api";
import { Fabric, Node } from "src/gen/schema/models/models";
import { CommandStatus } from "src/gen/schema/schema/schema";

export const getDevice = (path: string, fabric: Fabric | undefined) => {
  const [, switchId] = new RegExp(/\/node\/([^/]+)/).exec(path) ?? [];
  const [, serverId] = new RegExp(/\/server\/([^/]+)/).exec(path) ?? [];
  return fabric?.nodes.find((node: Node) => {
    return node.id === (switchId || serverId);
  });
};

export const moveCursorForwardByWord = (
  inputRef: React.RefObject<HTMLInputElement>,
  command: string
) => {
  if (inputRef.current) {
    const currCursor = inputRef.current.selectionStart || 0;
    const commandFromCursorPos = command.slice(currCursor);
    const commandList = commandFromCursorPos.split(" ");
    const newCursorPos = currCursor + (commandList?.[0]?.length ?? 0) + 1;
    inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
  }
};

export const moveCursorBackwardByWord = (
  inputRef: React.RefObject<HTMLInputElement>,
  command: string
) => {
  if (inputRef.current) {
    const input = inputRef.current;
    const start = input.selectionStart || 0;
    const previousSpace = command.lastIndexOf(" ", start - 2);
    const previousWordStart = previousSpace === -1 ? 0 : previousSpace + 1;
    input.setSelectionRange(previousWordStart, previousWordStart);
  }
};

/**
 * This is to provide auto complete function
 * where the word/letter at which the cursor is positioned at
 * gets autocompleted when user hits "space" if at all there is
 * any suggested word from backend. If not, its treated as
 * normal space.
 *
 */
export const getCommandCompletionData = (
  inputRef: React.RefObject<HTMLInputElement>,
  commandCompleteSuggestions: CliCommandCompletion[] | undefined,
  suggestion?: string
) => {
  const cursorPos = inputRef.current?.selectionStart || 0;
  const command = inputRef.current?.value || "";
  const textBeforeReplacementWord = command.slice(0, cursorPos - 1).split(" ");
  textBeforeReplacementWord.pop(); // Remove the first half current incomplete word
  const textAfterReplacementWord = command.slice(cursorPos).split(" ");
  textAfterReplacementWord.shift(); // Remove the next half current incomplete word
  const suggestedKeyword = suggestion
    ? suggestion
    : commandCompleteSuggestions
    ? commandCompleteSuggestions?.[0]?.keyword
    : "";
  let newValue, newCursorPos;
  if (suggestedKeyword) {
    const textTillSuggestedKeyword = `${textBeforeReplacementWord.join(
      " "
    )} ${suggestedKeyword}`.trimStart();
    newCursorPos = textTillSuggestedKeyword.length + 1;
    newValue = `${textBeforeReplacementWord.join(
      " "
    )} ${suggestedKeyword} ${textAfterReplacementWord.join(" ")}`.trimStart();
  }
  return { newValue, newCursorPos };
};
/**
 * This is to handle auto complete when there are multiple options
 * where the word/letter at which the cursor is positioned at
 * does not gets autocompleted when user hits "space", instead shows all
 * the options available that the command can take up.
 *
 * This function is to remove off the space or the keyword when user hits space,
 * to help user continue typing.
 */
export const getAmbiguousAutoComplete = (
  inputRef: React.RefObject<HTMLInputElement>
) => {
  const cursorPos = inputRef.current?.selectionStart || 0;
  const command = inputRef.current?.value || "";
  const textTillCursor = command.slice(0, cursorPos - 1);
  const textAfterReplacementWord = command.slice(cursorPos).split(" ");
  textAfterReplacementWord.shift(); // Remove the next half current incomplete word
  const newValue = `${textTillCursor} ${textAfterReplacementWord.join(" ")}`;
  const newCursorPos = textTillCursor.length;
  return { newValue, newCursorPos };
};

export const getMockShellHelp = () => {
  return {
    tab: "Autocompletes partial command and shows available options",
    "?": "Shows options for next command with help string",
    return: "Executes command until the cursor"
  };
};

export const getMockShellCtrlKeysHelp = () => {
  return {
    a: "Move to beginning of line",
    e: "Move to end of line",
    k: "Kill (cut) text from cursor to end of line",
    y: "Yank (paste) most recently killed text",
    p: "Move to previous command in history",
    n: "Move to next command in history",
    c: "Terminate a running output"
  };
};

export const getMockShellArrowKeysHelp = () => {
  return {
    "↑": "Move to previous command in history",
    "↓": "Move to next command in history"
  };
};

export const getMockShellMetaKeysHelp = () => {
  return {
    f: "Move forward 1 word",
    b: "Move backward 1 word"
  };
};

/**
 * Copies Mock Shell command output to clipboard in both HTML and plain text formats.
 * Falls back to plain text only if dual-format copy fails.
 * @param ref - React ref to the div element containing the command output
 */
export const copyMockshellCmds = (ref: RefObject<HTMLDivElement> | null) => {
  const element = ref?.current;
  if (!element || !navigator.clipboard) {
    return;
  }

  const htmlContent = element.innerHTML;
  const plainText = element.innerText;

  const clipboardItem = new ClipboardItem({
    "text/html": new Blob([htmlContent], { type: "text/html" }),
    "text/plain": new Blob([plainText], { type: "text/plain" })
  });

  navigator.clipboard.write([clipboardItem]).catch(() => {
    navigator.clipboard.writeText(plainText);
  });
};

export const getEmptyStateText = (
  totalRecords: number,
  streamingOutput: string,
  terminalStatus: CommandStatus | undefined
): string | undefined => {
  if (totalRecords !== 0 || streamingOutput) {
    return undefined;
  }

  switch (terminalStatus) {
    case CommandStatus.COMMAND_STATUS_KILLED:
      return "Command stopped";
    case CommandStatus.COMMAND_STATUS_FAILED:
      return "Command failed with no output";
    case CommandStatus.COMMAND_STATUS_EXPIRED:
      return "Command expired with no output";
    case CommandStatus.COMMAND_STATUS_COMPLETED:
      return "Command completed with no output";
    default:
      return undefined;
  }
};

export const buildMockShellPopOutUrl = (
  fabricId: string,
  command?: string,
  deviceId?: string
): string => {
  const basePath = `/${PATHS.FABRIC}/${fabricId}/${PATHS.MOCK_SHELL}`;
  const url = new URL(basePath, window.location.origin);
  if (command) url.searchParams.set("command", command);
  if (deviceId) url.searchParams.set("deviceId", deviceId);
  return url.pathname + url.search;
};
